import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  Link,
  Chip,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Download as DownloadIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Launch as LaunchIcon,
  Description as FileIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Cloud as CloudIcon
} from '@mui/icons-material';

const ExcelAddinTab: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [downloadStarted, setDownloadStarted] = useState(false);

  const handleDownloadManifest = async () => {
    try {
      setDownloadStarted(true);
      
      // Download the manifest file
      const response = await fetch('/api/v1/tariff-classifier/excel/manifest.xml');
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'tariff-classifier-addin.xml';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Move to next step
      setActiveStep(1);
    } catch (error) {
      console.error('Error downloading manifest:', error);
      alert('Failed to download Excel Add-in. Please try again.');
    }
  };

  const steps = [
    {
      label: 'Download Excel Add-in',
      description: 'Download the manifest file to install the add-in',
      content: (
        <Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Click the button below to download the Excel Add-in manifest file. This file tells Excel how to load our AI Tariff Classifier.
          </Typography>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadManifest}
            size="large"
            sx={{ mt: 1 }}
          >
            Download Excel Add-in
          </Button>
          {downloadStarted && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Manifest file downloaded! The file "tariff-classifier-addin.xml" should be in your Downloads folder.
              </Typography>
            </Alert>
          )}
        </Box>
      )
    },
    {
      label: 'Install in Excel',
      description: 'Load the add-in into Excel',
      content: (
        <Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Follow these steps to install the add-in in Excel:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><span style={{ fontWeight: 'bold' }}>1.</span></ListItemIcon>
              <ListItemText primary="Open Microsoft Excel (Desktop or Online)" />
            </ListItem>
            <ListItem>
              <ListItemIcon><span style={{ fontWeight: 'bold' }}>2.</span></ListItemIcon>
              <ListItemText primary="Go to Insert tab → Office Add-ins (or Get Add-ins)" />
            </ListItem>
            <ListItem>
              <ListItemIcon><span style={{ fontWeight: 'bold' }}>3.</span></ListItemIcon>
              <ListItemText primary="Click 'Upload My Add-in' or 'Manage My Add-ins' → 'Upload My Add-in'" />
            </ListItem>
            <ListItem>
              <ListItemIcon><span style={{ fontWeight: 'bold' }}>4.</span></ListItemIcon>
              <ListItemText primary="Select the downloaded 'tariff-classifier-addin.xml' file" />
            </ListItem>
            <ListItem>
              <ListItemIcon><span style={{ fontWeight: 'bold' }}>5.</span></ListItemIcon>
              <ListItemText primary="Click 'Upload' - Excel will validate and install the add-in" />
            </ListItem>
          </List>
          <Button
            variant="outlined"
            onClick={() => setActiveStep(2)}
            sx={{ mt: 2 }}
          >
            I've Installed the Add-in
          </Button>
        </Box>
      )
    },
    {
      label: 'Start Using',
      description: 'Use the add-in to classify products',
      content: (
        <Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Once installed, you'll see "AI Tariff Classifier" in your Excel ribbon. Here's how to use it:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
              <ListItemText 
                primary="Click the 'AI Tariff Classifier' button in Excel's Home tab"
                secondary="This opens the classification sidebar"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
              <ListItemText 
                primary="Select cells containing product descriptions"
                secondary="The add-in will detect your selection automatically"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
              <ListItemText 
                primary="Click 'Classify Selected Products'"
                secondary="AI will process your products and add results to adjacent columns"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
              <ListItemText 
                primary="Review results with color-coded confidence scores"
                secondary="Green (90%+), Yellow (70-90%), Red (<70%)"
              />
            </ListItem>
          </List>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Tip:</strong> Use the "Insert Sample Data" button in the add-in to try it out with example products!
            </Typography>
          </Alert>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Excel Add-in
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Use AI-powered tariff classification directly in Microsoft Excel. Perfect for processing large datasets and integrating with existing workflows.
        </Typography>
      </Box>

      {/* Features Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <SpeedIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Batch Processing
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select entire columns and classify hundreds of products with a single click
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <SecurityIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Confidence Scores
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Color-coded confidence levels help you identify which results need review
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <FileIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Detailed Reasoning
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click "View Reasoning" links to see complete AI analysis for each classification
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CloudIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Real-time Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Live progress tracking shows classification status as it processes your data
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
 
      {/* Installation Steps */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Installation & Setup
        </Typography>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>
                <Typography variant="h6">{step.label}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </StepLabel>
              <StepContent>
                {step.content}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>
    </Box>
  );
};

export default ExcelAddinTab;